import { Extension } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import { Decoration, DecorationSet, type EditorView } from "@tiptap/pm/view";

/*
 * Evernote's "Collapsible sections" (note ··· menu): every top-level heading gets a chevron in the
 * left margin; folding it hides the blocks under it up to the next heading of the same or a higher
 * level. Only decorations change, never the note, so folding doesn't edit or sync anything.
 * Folded headings are remembered per device (localStorage), keyed by level + text + occurrence so
 * they survive edits elsewhere in the note.
 */

interface State {
  enabled: boolean;
  folded: Set<string>;
  deco: DecorationSet;
}

export const foldKey = new PluginKey<State>("engramFolds");
const STORE = "engram.folds";

function loadFolds(noteId: string): Set<string> {
  try {
    const all = JSON.parse(localStorage.getItem(STORE) ?? "{}") as Record<string, string[]>;
    return new Set(all[noteId] ?? []);
  } catch {
    return new Set();
  }
}

function saveFolds(noteId: string, folded: Set<string>) {
  try {
    const all = JSON.parse(localStorage.getItem(STORE) ?? "{}") as Record<string, string[]>;
    if (folded.size) all[noteId] = [...folded];
    else delete all[noteId];
    localStorage.setItem(STORE, JSON.stringify(all));
  } catch {
    /* storage blocked: folds just aren't remembered */
  }
}

/** Top-level headings with their fold key, position and level. */
export function headings(doc: PMNode): { key: string; pos: number; level: number; node: PMNode }[] {
  const seen = new Map<string, number>();
  const out: { key: string; pos: number; level: number; node: PMNode }[] = [];
  doc.forEach((node, pos) => {
    if (node.type.name !== "heading") return;
    const base = `${node.attrs.level}|${node.textContent.trim()}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    out.push({ key: `${base}|${n}`, pos, level: node.attrs.level as number, node });
  });
  return out;
}

function build(doc: PMNode, enabled: boolean, folded: Set<string>): DecorationSet {
  if (!enabled) return DecorationSet.empty;
  const decos: Decoration[] = [];
  const heads = new Map(headings(doc).map((h) => [h.pos, h]));
  let hideUnder = 0; // level of the folded heading we're inside; 0 = showing
  doc.forEach((node, pos) => {
    const h = heads.get(pos);
    if (h && (!hideUnder || h.level <= hideUnder)) {
      hideUnder = 0;
      const isFolded = folded.has(h.key);
      if (isFolded) hideUnder = h.level;
      decos.push(Decoration.node(pos, pos + node.nodeSize, { class: isFolded ? "fold-head folded" : "fold-head" }));
      return;
    }
    if (hideUnder) decos.push(Decoration.node(pos, pos + node.nodeSize, { class: "fold-hidden" }));
  });
  return DecorationSet.create(doc, decos);
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    folds: {
      setFoldsEnabled: (enabled: boolean) => ReturnType;
      toggleFold: (key: string) => ReturnType;
      unfoldAll: () => ReturnType;
    };
  }
}

type Meta = { enabled?: boolean; toggle?: string; clear?: boolean; unfold?: string[] };

/** The chevron sits in the heading's left margin (CSS ::before); a press there toggles the fold. */
function chevronHit(view: EditorView, event: MouseEvent): string | null {
  const el = (event.target as HTMLElement).closest?.(".fold-head") as HTMLElement | null;
  if (!el || event.clientX >= el.getBoundingClientRect().left) return null;
  const pos = view.posAtDOM(el, 0) - 1;
  return headings(view.state.doc).find((h) => h.pos === pos)?.key ?? null;
}

export const Folds = Extension.create<{ noteId: string; enabled: boolean }>({
  name: "folds",
  addOptions() {
    return { noteId: "", enabled: false };
  },
  addCommands() {
    const meta =
      (m: Meta) =>
      ({ tr, dispatch }: { tr: Transaction; dispatch?: unknown }) => {
        if (dispatch) tr.setMeta(foldKey, m).setMeta("addToHistory", false);
        return true;
      };
    return {
      setFoldsEnabled: (enabled) => meta({ enabled }),
      toggleFold: (key) => meta({ toggle: key }),
      unfoldAll: () => meta({ clear: true }),
    };
  },
  addProseMirrorPlugins() {
    const { noteId } = this.options;
    return [
      new Plugin<State>({
        key: foldKey,
        state: {
          init: (_, { doc }) => {
            const folded = loadFolds(noteId);
            return { enabled: this.options.enabled, folded, deco: build(doc, this.options.enabled, folded) };
          },
          apply(tr, prev, _old, state) {
            const m = tr.getMeta(foldKey) as Meta | undefined;
            if (!m && !tr.docChanged) return prev;
            const enabled = m?.enabled ?? prev.enabled;
            let folded = prev.folded;
            if (m?.toggle || m?.clear || m?.unfold) {
              folded = new Set(m.clear ? [] : prev.folded);
              if (m.toggle) {
                if (folded.has(m.toggle)) folded.delete(m.toggle);
                else folded.add(m.toggle);
              }
              for (const k of m.unfold ?? []) folded.delete(k);
              saveFolds(noteId, folded);
            }
            return { enabled, folded, deco: build(state.doc, enabled, folded) };
          },
        },
        // The caret never stays in hidden text: Enter at the end of a folded heading, arrows, or a jump from the
        // table of contents into a folded section unfold what hides it.
        appendTransaction(trs, _old, state) {
          if (!trs.some((t) => t.selectionSet || t.docChanged)) return null;
          const st = foldKey.getState(state);
          if (!st?.enabled || !st.folded.size) return null;
          const unfold = foldsHiding(state.doc, st.folded, state.selection.head);
          return unfold.length ? state.tr.setMeta(foldKey, { unfold }).setMeta("addToHistory", false) : null;
        },
        props: {
          decorations: (state) => foldKey.getState(state)?.deco,
          handleDOMEvents: {
            mousedown(view, event) {
              if (!foldKey.getState(view.state)?.enabled) return false;
              const key = chevronHit(view, event);
              if (!key) return false;
              event.preventDefault();
              view.dispatch(view.state.tr.setMeta(foldKey, { toggle: key }).setMeta("addToHistory", false));
              return true;
            },
          },
        },
      }),
    ];
  },
});

/** Keys of the folded headings whose hidden range contains `pos` (inside a heading's own text isn't hidden). */
function foldsHiding(doc: PMNode, folded: Set<string>, pos: number): string[] {
  const heads = headings(doc);
  return heads
    .filter((h, i) => {
      if (!folded.has(h.key) || pos < h.pos + h.node.nodeSize) return false;
      const end = heads.slice(i + 1).find((o) => o.level <= h.level)?.pos ?? Infinity;
      return pos < end;
    })
    .map((h) => h.key);
}

/** Unfolds whatever hides `pos` (Find in note jumping to a hit inside a folded section). */
export function revealPos(view: EditorView, pos: number) {
  const st = foldKey.getState(view.state);
  if (!st?.enabled || !st.folded.size) return;
  const unfold = foldsHiding(view.state.doc, st.folded, pos);
  if (unfold.length) view.dispatch(view.state.tr.setMeta(foldKey, { unfold }).setMeta("addToHistory", false));
}
